import * as React from 'react';
import { FunctionComponent } from 'react';
import propz from 'propz';
import { PUBLIC_SITE_STATUS_OPTIONS } from '../../consts/tournament';
import { Tournament } from '../../models/tournament';
import { AppUser } from 'Src/views/App/App';
import {
  getSUPublicSiteTournamentPage,
  getTournamentPublicSiteLink,
  getTournamentTVPublicSiteLink
} from '../../helpers/link/link';

interface Props {
  tournament: Tournament;
  user: AppUser;
}

const ROWS = [
  { title: "Tournament's public website access", field: 'publicSiteStatus' },
  { title: "Tournament's public website access password", field: 'publicSitePassword' },
  { title: "Tournament's public website domain name", field: 'domain' },
  { title: "Tournament's public website external link", field: 'link' },
  { title: "Text of disclaimer on tournament's website", field: 'disclaimer' }
];

export const TournamentPublicSiteSettingsSummary: FunctionComponent<Props> = props => {
  const { tournament, user } = props;

  const tournamentPublicSiteLink = getTournamentPublicSiteLink(tournament),
    tournamentTVPublicSiteLink = getTournamentTVPublicSiteLink(tournament),
    suPublicSiteTournamentPage = getSUPublicSiteTournamentPage(tournament, user),
    activeSchoolDomain = user.activeSchool.domain;

  const rows = ROWS.map((row, index) => {
    let contentCell: object | string;
    const field = row.field;
    let isTournamentTVlink;
    let isExternalSite;

    switch (field) {
      case 'isPublishOnTheWebsite':
        contentCell = tournament.isPublishOnTheWebsite ? 'yes' : 'no';
        break;
      case 'isPromoteTournament':
        contentCell = tournament.isPromoteTournament ? 'yes' : 'no';
        break;
      case 'publicSiteStatus':
        contentCell = PUBLIC_SITE_STATUS_OPTIONS[tournament.publicSite.status];
        break;
      case 'publicSitePassword':
        contentCell = propz.get(tournament, ['publicSite', 'password'], '');
        break;
      case 'allowedForFollowing':
        contentCell = tournament.allowedForFollowing ? 'yes' : 'no';
        break;
      case 'domain':
        contentCell = propz.get(tournament, ['domain'], '');
        break;
      case 'link':
        contentCell = propz.get(tournament, ['link'], '');
        break;
      case 'disclaimer':
        contentCell = propz.get(tournament, ['disclaimer'], '');
        break;
      case 'publicBigscreenSiteStatus':
        contentCell = PUBLIC_SITE_STATUS_OPTIONS[tournament.publicBigscreenSite.status];
        break;
      case 'publicBigscreenSitePassword':
        contentCell = propz.get(tournament, ['publicBigscreenSite', 'password'], '');
        break;
      case 'publicBigscreenSiteAutoScrollingDelay':
        contentCell = propz.get(tournament, ['publicBigscreenSite', 'autoScrollingDelay'], '');
        break;
      case 'publicWebsiteLink':
        contentCell =
          typeof tournament.domain !== 'undefined' && tournament.domain !== '' ? (
            <a href={tournamentPublicSiteLink} target="_blank">
              {tournamentPublicSiteLink}
            </a>
          ) : (
            'no'
          );
        break;
      case 'publicWebsiteTvLink':
        isTournamentTVlink = true;
        isExternalSite = true;
        contentCell =
          typeof tournament.domain !== 'undefined' && tournament.domain !== '' ? (
            <a href={tournamentTVPublicSiteLink} target="_blank">
              {tournamentTVPublicSiteLink}
            </a>
          ) : (
            'no'
          );
        break;
      case 'detailsBookingPage':
        isExternalSite = false;
        contentCell =
          typeof activeSchoolDomain !== 'undefined' && activeSchoolDomain !== '' ? (
            <a href={suPublicSiteTournamentPage} target="_blank">
              {suPublicSiteTournamentPage}
            </a>
          ) : (
            'no'
          );
        break;
      default:
        contentCell = propz.get(tournament, [row.field], '');
    }

    return (
      <tr key={`tournament_PublicSiteSettings_summary_${index}`}>
        <td>{row.title}</td>
        <td>{contentCell}</td>
      </tr>
    );
  });

  const renderStatusPill = (value: string) => {
    const normalized = String(value || '').toLowerCase();

    let className = 'tournamentSummaryPill tournamentSummaryPill--subtle';

    if (normalized === 'yes') {
      className = 'tournamentSummaryPill tournamentSummaryPill--yes';
    } else if (normalized === 'no') {
      className = 'tournamentSummaryPill tournamentSummaryPill--no';
    } else if (normalized === 'enabled') {
      className = 'tournamentSummaryPill tournamentSummaryPill--enabled';
    } else if (normalized === 'disabled') {
      className = 'tournamentSummaryPill tournamentSummaryPill--disabled';
    }

    return <span className={className}>{value}</span>;
  };

  return (
    <div className="tournamentSummaryBody">
      <div className="tournamentSummaryItem">
        <div className="tournamentSummaryItemLabel">Public links</div>
        <div className="tournamentSummaryItemValue">
          <div className="tournamentSummaryLinkStack">
            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Public website</span>
              <span className="tournamentSummaryInlineGroupValue">
                {typeof tournament.domain !== 'undefined' && tournament.domain !== '' ? (
                  <a href={tournamentPublicSiteLink} target="_blank" rel="noopener noreferrer">
                    {tournamentPublicSiteLink}
                  </a>
                ) : (
                  renderStatusPill('No')
                )}
              </span>
            </span>

            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Tournament TV</span>
              <span className="tournamentSummaryInlineGroupValue">
                {typeof tournament.domain !== 'undefined' && tournament.domain !== '' ? (
                  <a href={tournamentTVPublicSiteLink} target="_blank" rel="noopener noreferrer">
                    {tournamentTVPublicSiteLink}
                  </a>
                ) : (
                  renderStatusPill('No')
                )}
              </span>
            </span>

            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Details / booking page</span>
              <span className="tournamentSummaryInlineGroupValue">
                {typeof activeSchoolDomain !== 'undefined' && activeSchoolDomain !== '' ? (
                  <a href={suPublicSiteTournamentPage} target="_blank" rel="noopener noreferrer">
                    {suPublicSiteTournamentPage}
                  </a>
                ) : (
                  renderStatusPill('No')
                )}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="tournamentSummaryItem">
        <div className="tournamentSummaryItemLabel">Visibility and options</div>
        <div className="tournamentSummaryItemValue">
          <div className="tournamentSummaryInlineGroups">
            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Publish</span>
              <span className="tournamentSummaryInlineGroupValue">
                {renderStatusPill(tournament.isPublishOnTheWebsite ? 'Yes' : 'No')}
              </span>
            </span>

            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Highlight tournament</span>
              <span className="tournamentSummaryInlineGroupValue">
                {renderStatusPill(tournament.isPromoteTournament ? 'Yes' : 'No')}
              </span>
            </span>

            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Allowed for following</span>
              <span className="tournamentSummaryInlineGroupValue">
                {renderStatusPill(tournament.allowedForFollowing ? 'Yes' : 'No')}
              </span>
            </span>

          </div>
        </div>
      </div>

      <div className="tournamentSummaryGrid">
        {ROWS.map((row, index) => {
          let contentCell: React.ReactNode;
          const field = row.field;

          switch (field) {
            case 'publicSiteStatus':
              contentCell = renderStatusPill(PUBLIC_SITE_STATUS_OPTIONS[tournament.publicSite.status]);
              break;
            case 'publicSitePassword':
              contentCell = propz.get(tournament, ['publicSite', 'password'], '') || '—';
              break;
            case 'domain':
              contentCell = propz.get(tournament, ['domain'], '') || '—';
              break;
            case 'link':
              contentCell = propz.get(tournament, ['link'], '') || '—';
              break;
            case 'disclaimer':
              contentCell = propz.get(tournament, ['disclaimer'], '') || '—';
              break;
            case 'publicBigscreenSiteStatus':
              contentCell = renderStatusPill(PUBLIC_SITE_STATUS_OPTIONS[tournament.publicBigscreenSite.status]);
              break;
            case 'publicBigscreenSitePassword':
              contentCell = propz.get(tournament, ['publicBigscreenSite', 'password'], '') || '—';
              break;
            case 'publicBigscreenSiteAutoScrollingDelay':
              contentCell = propz.get(tournament, ['publicBigscreenSite', 'autoScrollingDelay'], '') || '—';
              break;
            default:
              contentCell = propz.get(tournament, [row.field], '') || '—';
          }

          return (
            <div
              key={`tournament_PublicSiteSettings_summary_${index}`}
              className="tournamentSummaryItem"
              style={field === 'disclaimer' ? { gridColumn: '1 / -1' } : undefined}
            >
              <div className="tournamentSummaryItemLabel">{row.title}</div>
              <div className="tournamentSummaryItemValue">{contentCell}</div>
            </div>
          );
        })}
      </div>
      <div className="tournamentSummaryItem">
        <div className="tournamentSummaryItemLabel">Bigscreen settings</div>
        <div className="tournamentSummaryItemValue">
          <div className="tournamentSummaryInlineGroups">
            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Access</span>
              <span className="tournamentSummaryInlineGroupValue">
                {renderStatusPill(PUBLIC_SITE_STATUS_OPTIONS[tournament.publicBigscreenSite.status])}
              </span>
            </span>

            <span className="tournamentSummaryInlineGroup">
              <span className="tournamentSummaryInlineGroupLabel">Password</span>
              <span className="tournamentSummaryInlineGroupValue">
                {propz.get(tournament, ['publicBigscreenSite', 'password'], '') || '—'}
              </span>
            </span>

          </div>
        </div>
      </div>
    </div>
  );
};

TournamentPublicSiteSettingsSummary.displayName = 'TournamentPublicSiteSettingsSummary';

